import {
    CognitoUser as _CognitoUser,
    CognitoUserPool,
    CognitoUserSession,
    ICognitoUserData,
    ICognitoUserPoolData,
} from 'amazon-cognito-identity-js';
import { getLocalStorage } from './localStorage';

class CognitoUser {
    poolData: ICognitoUserPoolData | undefined;
    userPool: CognitoUserPool | undefined;
    userData: ICognitoUserData | undefined;
    cognitoUser: _CognitoUser | undefined;
    cognitoUserSession: CognitoUserSession | undefined;
    sessionUserAttributes: Record<string, string> | undefined;
    // Name of the tenant that refused the sign-in until an authenticator is enrolled; shown on the
    // enrolment screen, which runs before any portal session exists.
    mfaSetupTenantName: string | undefined;
    // The address the user signed in with. After an SRP round trip the Cognito user object reports the
    // pool's internal username (the `sub`) instead, which is what the code-entry screens would otherwise show.
    signInEmail: string | undefined;

    constructor(username: string) {
        if (!username) {
            return;
        }

        this.poolData = {
            UserPoolId: import.meta.env.VITE_COGNITO_POOL_ID || '', // Cognito user pool id
            ClientId: import.meta.env.VITE_COGNITO_APP_CLIENT_ID || '', // Cognito App client id
        };

        this.userPool = new CognitoUserPool(this.poolData);

        this.userData = {
            Username: username,
            Pool: this.userPool,
        };

        this.cognitoUser = new _CognitoUser(this.userData);
        this.cognitoUser.getSession((error: Error, session: CognitoUserSession | null) => {
            if (error) {
                return;
            } else {
                if (session) {
                    this.cognitoUserSession = session;
                }
            }
        });
    }

    public getCognitoUser(): _CognitoUser | undefined {
        return this.cognitoUser;
    }

    public setCognitoUser(user: _CognitoUser): void {
        this.cognitoUser = user;
    }

    public getSession(): Promise<CognitoUserSession | undefined> {
        return new Promise((resolve, reject) => {
            if (this.cognitoUser) {
                this.cognitoUser.getSession((error: Error, session: CognitoUserSession | null) => {
                    if (error) {
                        console.error('Error getting user session: ', error);
                        this.cognitoUserSession = undefined;
                        reject(error);
                    } else {
                        if (session && session !== null) {
                            this.cognitoUserSession = session;
                            resolve(session);
                        } else {
                            this.cognitoUserSession = undefined;
                            resolve(undefined);
                        }
                    }
                });
            } else {
                resolve(undefined);
            }
        });
    }

    public setCognitoUserSession(session: CognitoUserSession): void {
        this.cognitoUserSession = session;
    }

    public getSessionUserAttributes(): Record<string, string> | undefined {
        return this.sessionUserAttributes;
    }

    public setSessionUserAttributes(attributes: Record<string, string>): void {
        this.sessionUserAttributes = attributes;
    }

    public getMfaSetupTenantName(): string | undefined {
        return this.mfaSetupTenantName;
    }

    public setMfaSetupTenantName(tenantName: string | undefined): void {
        this.mfaSetupTenantName = tenantName;
    }

    public getSignInEmail(): string | undefined {
        return this.signInEmail;
    }

    public setSignInEmail(email: string): void {
        this.signInEmail = email;
    }

    public clear(): void {
        this.cognitoUser = undefined;
        this.cognitoUserSession = undefined;
        this.sessionUserAttributes = undefined;
        this.mfaSetupTenantName = undefined;
        this.signInEmail = undefined;
    }
}

// Instatiate the Cognito user object to hold the reference of the user
export const cognitoUser = new CognitoUser(getLocalStorage('cognito_user') || '');
